// Global Instructions Rule Applied!

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { SettingOutlined } from '@ant-design/icons'
import {
  Alert,
  Button,
  Checkbox,
  Empty,
  Input,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Table } from 'antd'
import { useTopicSubscriptions } from '../hooks/useTopicSubscriptions.ts'
import {
  BulkAccount,
  BulkContact,
  BulkFilterOption
} from '../../../state_manager/data_models/bulk_account_topics_grid_model.ts'
import { BulkAccountTopicsFilters } from '../../../state_manager/interfaces/distribution_lists.ts'

const { Title, Text } = Typography

type SelMap = Record<string, Set<string>>
type AcctSel = Record<string, Set<string>>
type ContactSel = Record<string, SelMap>

// One consolidated Ant Table (architect UI review 2026-05-17): topic columns
// are defined ONCE so the header renders once at the top (not repeated per
// account), with account rows + expandable contact children (tree data).
//
// Perf architecture (option B, 2026-05-17): the per-(account|contact, topic)
// checked/disabled booleans and the account's dirty flag are embedded INTO
// the row record, built with structural sharing (an unchanged account keeps
// its exact row object across renders). Combined with selection-independent
// (stable) `columns`, AntD then re-renders only the row whose selection
// actually changed — a toggle no longer re-renders the whole accumulated
// tree, and an infinite-scroll append re-renders only the new rows. This is
// the standard "render from data + memoized cells" pattern (no new infra,
// no virtualization), removing the prior columns-rebuilt-on-selection
// anti-pattern.
type AccountRow = {
  key: string
  kind: 'account'
  accountId: string
  account: BulkAccount
  acctChecked: Record<string, boolean>
  isDirty: boolean
  children: ContactRow[]
}
type ContactRow = {
  key: string
  kind: 'contact'
  accountId: string
  contact: BulkContact
  checked: Record<string, boolean>
  disabled: Record<string, boolean>
}
type Row = AccountRow | ContactRow

// Memoized topic checkbox cell — re-renders only when its own checked /
// disabled changes (onChange is semantically stable: it calls the stable
// toggle callbacks with fixed ids, so it's intentionally excluded from the
// equality check).
const TopicCheckCell = React.memo(
  function TopicCheckCell(props: { checked: boolean; disabled: boolean; onChange: () => void }) {
    return <Checkbox checked={props.checked} disabled={props.disabled} onChange={props.onChange} />
  },
  (p, n) => p.checked === n.checked && p.disabled === n.disabled
)

const accountSnapshot = (acct: Set<string>, contacts: SelMap): string => {
  const a = Array.from(acct).sort().join(',')
  const c = Object.keys(contacts)
    .sort()
    .map((k) => `${k}:${Array.from(contacts[k]).sort().join(',')}`)
    .join('|')
  return `${a}#${c}`
}

const seedAccount = (a: BulkAccount): { acct: Set<string>; contacts: SelMap; base: string } => {
  const acct = new Set(a.account_topics__comms_topic_ids ?? [])
  const contacts: SelMap = {}
  for (const c of a.contacts ?? []) {
    contacts[c.contacts__id] = new Set(c.contact_account_topics__comms_topic_ids ?? [])
  }
  return { acct, contacts, base: accountSnapshot(acct, contacts) }
}

const contactName = (c: BulkContact): string => {
  const full = [c.contacts__last_name, c.contacts__first_name].filter(Boolean).join(', ')
  return full || c.contacts__email || c.contacts__id
}

const selectFilter = (input: string, option?: { label?: string }) =>
  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

export function TopicSubscriptionsPage(): React.ReactElement {
  const {
    filters,
    applyFilters,
    accounts,
    resetToken,
    topics,
    count,
    hasMore,
    options,
    loading,
    loadingMore,
    saving,
    error,
    loadMore,
    refetch,
    save
  } = useTopicSubscriptions()

  // Topic Management no longer has its own left-nav item (architect ruling
  // 2026-05-18, §2.15g — it's rarely used). It is reached from here instead;
  // navigating to /topics opens the existing screen as a tab (route wiring
  // unchanged — the menu item was only removed from cfg.menu_items).
  const navigate = useNavigate()

  const [draft, setDraft] = useState<BulkAccountTopicsFilters>(filters)
  useEffect(() => {
    setDraft(filters)
  }, [filters])

  const [acctSel, setAcctSel] = useState<AcctSel>({})
  const [contactSel, setContactSel] = useState<ContactSel>({})
  const [baseline, setBaseline] = useState<Record<string, string>>({})
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])

  // Refs mirror current selection so the seeding effect can preserve edits
  // for already-loaded accounts when more pages append, without depending on
  // (and looping over) the selection state itself.
  const acctSelRef = useRef(acctSel)
  const contactSelRef = useRef(contactSel)
  const baselineRef = useRef(baseline)
  // Mirrors `accounts` so the toggle callbacks can be referentially STABLE
  // (empty-dep useCallback) — stable handlers are what let the memoized
  // cells / structural-shared rows skip re-rendering unchanged accounts.
  const accountsRef = useRef(accounts)
  useEffect(() => {
    accountsRef.current = accounts
  }, [accounts])
  useEffect(() => {
    acctSelRef.current = acctSel
  }, [acctSel])
  useEffect(() => {
    contactSelRef.current = contactSel
  }, [contactSel])
  useEffect(() => {
    baselineRef.current = baseline
  }, [baseline])

  // Seed selection as accounts arrive.
  //  - RESET (initial / applyFilters / refetch / post-save → resetToken
  //    bumped): reseed EVERY account from fresh DB truth, dropping prior
  //    edits. This is what clears the dirty flag after Save (the saved
  //    state becomes the new baseline == selection).
  //  - APPEND (infinite scroll, resetToken unchanged): keep already-loaded
  //    accounts' (possibly edited) selection so scrolling never drops
  //    edits; seed only the newly-appended accounts.
  const lastResetTokenRef = useRef<number>(-1)
  useEffect(() => {
    const isReset = lastResetTokenRef.current !== resetToken
    lastResetTokenRef.current = resetToken

    if (isReset) {
      // RESET: rebuild every account from fresh DB truth (drops prior
      // edits; this clears dirty after Save).
      const nextA: AcctSel = {}
      const nextC: ContactSel = {}
      const nextB: Record<string, string> = {}
      for (const a of accounts) {
        const s = seedAccount(a)
        nextA[a.accounts__id] = s.acct
        nextC[a.accounts__id] = s.contacts
        nextB[a.accounts__id] = s.base
      }
      setAcctSel(nextA)
      setContactSel(nextC)
      setBaseline(nextB)
      return
    }

    // APPEND (infinite scroll): seed ONLY the newly-arrived accounts and
    // MERGE them in — already-loaded accounts keep their (possibly edited)
    // selection AND object identity, so an append no longer reseeds /
    // re-allocates the whole accumulated selection (was O(total) + 3 full
    // setStates per page → a full tree re-render; now O(new), and a no-op
    // when nothing new arrived).
    const addA: AcctSel = {}
    const addC: ContactSel = {}
    const addB: Record<string, string> = {}
    let added = false
    for (const a of accounts) {
      const id = a.accounts__id
      if (baselineRef.current[id] === undefined) {
        const s = seedAccount(a)
        addA[id] = s.acct
        addC[id] = s.contacts
        addB[id] = s.base
        added = true
      }
    }
    if (added) {
      setAcctSel((prev) => ({ ...prev, ...addA }))
      setContactSel((prev) => ({ ...prev, ...addC }))
      setBaseline((prev) => ({ ...prev, ...addB }))
    }
  }, [accounts, resetToken])

  const dirtyAccountIds = useMemo(() => {
    const ids: string[] = []
    for (const a of accounts) {
      const id = a.accounts__id
      const snap = accountSnapshot(acctSel[id] ?? new Set(), contactSel[id] ?? {})
      if (snap !== (baseline[id] ?? '#')) ids.push(id)
    }
    return ids
  }, [accounts, acctSel, contactSel, baseline])
  const dirty = dirtyAccountIds.length > 0
  const dirtySet = useMemo(() => new Set(dirtyAccountIds), [dirtyAccountIds])

  // Toggling an account topic cascades to its eligible contacts, mirroring
  // the Phase 0 propagate trigger + the contact ⊆ account invariant
  // (plan §1): ON subscribes every eligible contact (so the save's
  // per-contact reconcile keeps the trigger-created rows instead of
  // soft-deleting them); OFF removes it from every contact. Individual
  // contacts can then be un-ticked as explicit exclusions.
  // Referentially STABLE (empty-dep useCallback, reads live state via refs)
  // so the memoized cells / structural-shared rows stay valid.
  const toggleAccount = useCallback((accountId: string, topicId: string) => {
    const turningOn = !(acctSelRef.current[accountId] ?? new Set<string>()).has(topicId)
    setAcctSel((prev) => {
      const next = new Set(prev[accountId] ?? [])
      if (turningOn) next.add(topicId)
      else next.delete(topicId)
      return { ...prev, [accountId]: next }
    })
    const acct = accountsRef.current.find((a) => a.accounts__id === accountId)
    const contactIds = (acct?.contacts ?? []).map((c) => c.contacts__id)
    setContactSel((prev) => {
      const acctMap = { ...(prev[accountId] ?? {}) }
      for (const cid of contactIds) {
        const s = new Set(acctMap[cid] ?? [])
        if (turningOn) s.add(topicId)
        else s.delete(topicId)
        acctMap[cid] = s
      }
      return { ...prev, [accountId]: acctMap }
    })
  }, [])

  const toggleContact = useCallback(
    (accountId: string, contactId: string, topicId: string) =>
      setContactSel((prev) => {
        const acctMap = { ...(prev[accountId] ?? {}) }
        const set = new Set(acctMap[contactId] ?? [])
        if (set.has(topicId)) set.delete(topicId)
        else set.add(topicId)
        acctMap[contactId] = set
        return { ...prev, [accountId]: acctMap }
      }),
    []
  )

  // Structural-sharing row builder: an account whose source row, its
  // selection Set refs, the topic set, and its dirty flag are all unchanged
  // keeps its EXACT prior row object (and children) — so a toggle rebuilds
  // only the affected account's row and an append builds only new accounts;
  // every other row keeps identity and AntD skips re-rendering it. The
  // per-cell checked/disabled booleans are embedded so `columns` need not
  // depend on selection state.
  const rowCacheRef = useRef<
    Map<
      string,
      {
        a: BulkAccount
        aSel: Set<string> | undefined
        cSel: SelMap | undefined
        tp: typeof topics
        dirty: boolean
        row: AccountRow
      }
    >
  >(new Map())

  const rows: Row[] = useMemo(() => {
    const out: AccountRow[] = []
    const liveIds = new Set<string>()
    for (const a of accounts) {
      const id = a.accounts__id
      liveIds.add(id)
      const aSel = acctSel[id]
      const cSel = contactSel[id]
      const isDirty = dirtySet.has(id)
      const cached = rowCacheRef.current.get(id)
      if (
        cached &&
        cached.a === a &&
        cached.aSel === aSel &&
        cached.cSel === cSel &&
        cached.tp === topics &&
        cached.dirty === isDirty
      ) {
        out.push(cached.row)
        continue
      }
      const acctChecked: Record<string, boolean> = {}
      for (const t of topics) acctChecked[t.comms_topics__id] = Boolean(aSel?.has(t.comms_topics__id))
      const children: ContactRow[] = (a.contacts ?? []).map((c) => {
        const cset = cSel?.[c.contacts__id]
        const checked: Record<string, boolean> = {}
        const disabled: Record<string, boolean> = {}
        for (const t of topics) {
          const tid = t.comms_topics__id
          checked[tid] = Boolean(cset?.has(tid))
          disabled[tid] = !acctChecked[tid] // contact cell off when account topic off
        }
        return {
          key: `${id}::${c.contacts__id}`,
          kind: 'contact' as const,
          accountId: id,
          contact: c,
          checked,
          disabled
        }
      })
      const row: AccountRow = {
        key: id,
        kind: 'account' as const,
        accountId: id,
        account: a,
        acctChecked,
        isDirty,
        children
      }
      rowCacheRef.current.set(id, { a, aSel, cSel, tp: topics, dirty: isDirty, row })
      out.push(row)
    }
    // Drop cache entries for accounts no longer present (filter/reset).
    for (const k of Array.from(rowCacheRef.current.keys())) {
      if (!liveIds.has(k)) rowCacheRef.current.delete(k)
    }
    return out
  }, [accounts, acctSel, contactSel, dirtySet, topics])

  const allAccountKeys = useMemo(() => accounts.map((a) => a.accounts__id), [accounts])
  const allExpanded = accounts.length > 0 && expandedKeys.length >= accounts.length
  const anyExpanded = expandedKeys.length > 0
  const expandAll = () => setExpandedKeys(allAccountKeys)
  const collapseAll = () => setExpandedKeys([])

  const columns: ColumnsType<Row> = useMemo(() => {
    const nameCol: ColumnsType<Row>[number] = {
      // Expand-all and collapse-all as TWO always-visible controls in the
      // FIRST column's header — inline with the topic headings (the standard
      // dual-control pattern; both available simultaneously). Tree data
      // renders the per-row caret inside this column, so there is no
      // separate expand column for expandable.columnTitle to use.
      title: (
        <div className='flex items-center gap-2'>
          <Space size={2}>
            <Tooltip title='Expand all'>
              <Button
                type='text'
                size='small'
                aria-label='Expand all'
                onClick={expandAll}
                disabled={accounts.length === 0 || allExpanded}
                style={{ padding: 0, width: 22, height: 22, lineHeight: '20px', fontWeight: 700 }}
              >
                +
              </Button>
            </Tooltip>
            <Tooltip title='Collapse all'>
              <Button
                type='text'
                size='small'
                aria-label='Collapse all'
                onClick={collapseAll}
                disabled={!anyExpanded}
                style={{ padding: 0, width: 22, height: 22, lineHeight: '18px', fontWeight: 700 }}
              >
                −
              </Button>
            </Tooltip>
          </Space>
          <span>Name</span>
        </div>
      ),
      key: 'name',
      fixed: 'left',
      width: 280,
      render: (_: unknown, row: Row) => {
        if (row.kind === 'account') {
          const a = row.account
          let subCount = 0
          for (const k in row.acctChecked) if (row.acctChecked[k]) subCount++
          return (
            <div className='flex items-center gap-2'>
              <Text strong>{a.accounts__name || '(unnamed account)'}</Text>
              {a.accounts__lp_status_label ? (
                <Tag color='blue'>{a.accounts__lp_status_label}</Tag>
              ) : null}
              <Text type='secondary' className='text-xs'>
                {subCount} topic{subCount === 1 ? '' : 's'} · {(a.contacts ?? []).length} contact
                {(a.contacts ?? []).length === 1 ? '' : 's'}
              </Text>
              {row.isDirty ? <Tag color='orange'>unsaved</Tag> : null}
            </div>
          )
        }
        const c = row.contact
        return (
          <div className='flex flex-col'>
            <Text>{contactName(c)}</Text>
            {c.contacts__email ? (
              <Text type='secondary' className='text-xs'>
                {c.contacts__email}
              </Text>
            ) : null}
          </div>
        )
      }
    }

    const topicCols: ColumnsType<Row> = topics.map((t) => ({
      title: t.comms_topics__label,
      key: t.comms_topics__id,
      align: 'center' as const,
      width: 150,
      render: (_: unknown, row: Row) => {
        const tid = t.comms_topics__id
        if (row.kind === 'account') {
          return (
            <TopicCheckCell
              checked={Boolean(row.acctChecked[tid])}
              disabled={saving}
              onChange={() => toggleAccount(row.accountId, tid)}
            />
          )
        }
        const cid = row.contact.contacts__id
        return (
          <TopicCheckCell
            checked={Boolean(row.checked[tid])}
            disabled={row.disabled[tid] || saving}
            onChange={() => toggleContact(row.accountId, cid, tid)}
          />
        )
      }
    }))

    return [nameCol, ...topicCols]
    // Selection-independent (stable across toggles/appends): the per-cell
    // checked/disabled + the dirty flag travel in the row record, so columns
    // need not depend on acctSel/contactSel/dirtyAccountIds. `toggleAccount`
    // / `toggleContact` are stable useCallbacks. eslint-disable for the
    // intentionally minimal dep list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topics, saving, allExpanded, anyExpanded, accounts.length])

  // Changing the filter set resets the grid from fresh DB truth, which would
  // silently drop unsaved edits — block it while dirty (the buttons are also
  // disabled; this guards the search box's Enter key).
  const handleApply = () => {
    if (dirty) return
    applyFilters(draft)
  }
  const handleClear = () => {
    if (dirty) return
    const empty: BulkAccountTopicsFilters = {
      role_ids: [],
      fund_ids: [],
      lp_status_ids: [],
      search: ''
    }
    setDraft(empty)
    applyFilters(empty)
  }

  const handleDiscard = () => {
    const nextA: AcctSel = {}
    const nextC: ContactSel = {}
    const nextB: Record<string, string> = {}
    for (const a of accounts) {
      const s = seedAccount(a)
      nextA[a.accounts__id] = s.acct
      nextC[a.accounts__id] = s.contacts
      nextB[a.accounts__id] = s.base
    }
    setAcctSel(nextA)
    setContactSel(nextC)
    setBaseline(nextB)
  }

  const handleSave = async () => {
    const byId = new Map(accounts.map((a) => [a.accounts__id, a]))
    const payloadAccounts = dirtyAccountIds.map((id) => {
      const a = byId.get(id)
      return {
        account_id: id,
        account_topic_ids: Array.from(acctSel[id] ?? []),
        contacts: (a?.contacts ?? []).map((c) => ({
          contact_id: c.contacts__id,
          comms_topic_ids: Array.from(contactSel[id]?.[c.contacts__id] ?? [])
        }))
      }
    })
    if (payloadAccounts.length === 0) return
    await save({ accounts: payloadAccounts })
  }

  // Infinite scroll: a sentinel after the table; when it scrolls into view
  // and there's another page, fetch + append (the hook accumulates).
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading && !loadingMore) {
          loadMore()
        }
      },
      { rootMargin: '200px' }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [hasMore, loading, loadingMore, loadMore])

  const toAntOptions = (o: BulkFilterOption[]) => o.map((x) => ({ value: x.value, label: x.label }))
  const roleOpts = options?.roles ?? []
  const fundOpts = options?.funds ?? []
  const lpOpts = options?.lp_status ?? []

  return (
    <div className='topic-subscriptions flex flex-col h-full p-4 gap-3 overflow-hidden'>
      <div className='flex items-center justify-between'>
        <Title level={4} className='!mb-0'>
          Topic Subscriptions
        </Title>
        <Space>
          <Tooltip title='Create or edit topics and their account-role auto-subscriptions'>
            <Button
              type='primary'
              icon={<SettingOutlined />}
              onClick={() => navigate('/topics')}
            >
              Manage Topics
            </Button>
          </Tooltip>
          {dirty && (
            <>
              <Button onClick={handleDiscard} disabled={saving}>
                Discard
              </Button>
              <Tooltip title={`${dirtyAccountIds.length} account(s) changed`}>
                <Button type='primary' onClick={handleSave} loading={saving}>
                  Save ({dirtyAccountIds.length})
                </Button>
              </Tooltip>
            </>
          )}
        </Space>
      </div>

      {/* Filter bar — role / fund / LP-status (multi) + name search. */}
      <div className='flex flex-wrap items-center gap-2'>
        <Select
          mode='multiple'
          allowClear
          className='min-w-[200px]'
          placeholder='Role'
          value={draft.role_ids}
          options={toAntOptions(roleOpts)}
          showSearch
          filterOption={selectFilter}
          maxTagCount='responsive'
          onChange={(v) => setDraft((p) => ({ ...p, role_ids: v }))}
        />
        <Select
          mode='multiple'
          allowClear
          className='min-w-[200px]'
          placeholder='Fund'
          value={draft.fund_ids}
          options={toAntOptions(fundOpts)}
          showSearch
          filterOption={selectFilter}
          maxTagCount='responsive'
          onChange={(v) => setDraft((p) => ({ ...p, fund_ids: v }))}
        />
        <Select
          mode='multiple'
          allowClear
          className='min-w-[180px]'
          placeholder='LP status'
          value={draft.lp_status_ids}
          options={toAntOptions(lpOpts)}
          showSearch
          filterOption={selectFilter}
          maxTagCount='responsive'
          onChange={(v) => setDraft((p) => ({ ...p, lp_status_ids: v }))}
        />
        <Input.Search
          allowClear
          className='max-w-[260px]'
          placeholder='Account name'
          value={draft.search}
          onChange={(e) => setDraft((p) => ({ ...p, search: e.target.value }))}
          onSearch={handleApply}
        />
        <Button type='primary' onClick={handleApply} disabled={dirty}>
          Apply
        </Button>
        <Button onClick={handleClear} disabled={dirty}>
          Clear
        </Button>
      </div>

      {dirty ? (
        <Alert
          type='warning'
          showIcon
          message='Unsaved changes — Save or Discard before changing filters.'
        />
      ) : null}

      {/* Slim toolbar: loaded-count (expand/collapse-all is the +/− control
          in the table header, inline with the column headings). */}
      <div className='flex items-center justify-end'>
        <Text type='secondary' className='text-xs'>
          {accounts.length} of {count} account{count === 1 ? '' : 's'} loaded
        </Text>
      </div>

      {error ? (
        <Alert
          type='error'
          showIcon
          message='Could not load or save topic subscriptions'
          description={error}
          action={
            <Button size='small' onClick={refetch}>
              Retry
            </Button>
          }
        />
      ) : null}

      <div className='flex-1 min-h-0 overflow-auto'>
        {loading && accounts.length === 0 ? (
          <div className='flex items-center justify-center min-h-[400px]'>
            <Spin size='large' />
          </div>
        ) : topics.length === 0 ? (
          <Empty
            description='No topics defined yet. Create topics in Topic Management.'
            className='my-10'
          />
        ) : accounts.length === 0 ? (
          <Empty description='No accounts match the current filters.' className='my-10' />
        ) : (
          <>
            <Table<Row>
              size='small'
              columns={columns}
              dataSource={rows}
              rowKey='key'
              pagination={false}
              sticky
              scroll={{ x: 'max-content' }}
              expandable={{
                expandedRowKeys: expandedKeys,
                onExpandedRowsChange: (keys) => setExpandedKeys(keys as string[]),
                rowExpandable: (row) => row.kind === 'account' && row.children.length > 0
              }}
              rowClassName={(row) => (row.kind === 'account' ? 'ts-account-row' : '')}
            />
            <div ref={sentinelRef} className='h-8 flex items-center justify-center'>
              {loadingMore ? <Spin size='small' /> : null}
            </div>
          </>
        )}
      </div>

      <style>{`
        .topic-subscriptions .ts-account-row > td {
          font-weight: 600;
        }
      `}</style>
    </div>
  )
}

export default TopicSubscriptionsPage
